import React, { createContext, useContext, useState } from 'react';
import { useGetCalendarStatus } from '@/core/protected/Integrations/Calendar/query';
import { IOption } from '@/components/Form/SelectField';
import { visibleCalendar } from '@/utils/texts';

interface CalendarProviderStatus {
  connected: boolean;
  isLoading: boolean;
  accountEmail: string | null;
}

interface CalendarContextValue {
  isCalendarConnected: boolean;
  isLoading: boolean;
  statuses: Record<string, CalendarProviderStatus>;
  connectedCalendars: IOption[];
  connectingProvider: string | null;
  setConnectingProvider: (provider: string | null) => void;
}

const CalendarContext = createContext<CalendarContextValue | null>(null);

const isVisibleProvider = (provider: string) => visibleCalendar.some(calendarItem => calendarItem.provider === provider);

export const CalendarProvider = ({ children }: { children: React.ReactNode }) => {
  const google = useGetCalendarStatus('google', isVisibleProvider('google'));
  const zoom = useGetCalendarStatus('zoom', isVisibleProvider('zoom'));
  const microsoft = useGetCalendarStatus('microsoft', isVisibleProvider('microsoft'));

  const statuses: Record<string, CalendarProviderStatus> = {
    google: {
      connected: Boolean(google.data?.connected),
      isLoading: google.isLoading,
      accountEmail: google.data?.account_email ?? null,
    },
    zoom: {
      connected: Boolean(zoom.data?.connected),
      isLoading: zoom.isLoading,
      accountEmail: zoom.data?.account_email ?? null,
    },
    microsoft: {
      connected: Boolean(microsoft.data?.connected),
      isLoading: microsoft.isLoading,
      accountEmail: microsoft.data?.account_email ?? null,
    },
  };

  const isCalendarConnected = visibleCalendar.some(calendarItem => statuses[calendarItem.provider]?.connected);
  const isLoading = visibleCalendar.some(calendarItem => statuses[calendarItem.provider]?.isLoading);

  const connectedCalendars: IOption[] = visibleCalendar
    .filter(calendarItem => statuses[calendarItem.provider]?.connected)
    .map(calendarItem => ({
      value: calendarItem.provider,
      label: calendarItem.title,
      secondaryValue: calendarItem.icon,
    }));

  const [connectingProvider, setConnectingProvider] = useState<string | null>(null);

  return (
    <CalendarContext.Provider
      value={{
        isCalendarConnected,
        isLoading,
        statuses,
        connectedCalendars,
        connectingProvider,
        setConnectingProvider,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendar = (): CalendarContextValue => {
  const ctx = useContext(CalendarContext);
  if (!ctx) throw new Error('useCalendar must be used inside CalendarProvider');
  return ctx;
};
